import React, { useState, useEffect, useRef } from 'react';
import { ShoppingList } from '../types';
import { ShoppingCart, Users, Calendar, Edit2, Trash2, MoreVertical, LogOut } from 'lucide-react';

interface ListCardProps {
  list: ShoppingList;
  onClick: () => void;
  onEdit?: (list: ShoppingList) => void;
  onDelete?: (list: ShoppingList) => void;
  onRemoveList?: (list: ShoppingList) => void;
  isOwner?: boolean;
}

const ListCard: React.FC<ListCardProps> = ({ list, onClick, onEdit, onDelete, onRemoveList, isOwner = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit?.(list);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete?.(list);
  };

  const handleRemoveList = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onRemoveList?.(list);
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="relative">
      <div
        onClick={onClick}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                {list.name}
              </h3>
              <p className="text-sm text-gray-500">Shopping List</p>
            </div>
          </div>
          
          <button
            onClick={handleMenuToggle}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4" />
            <span>Created {formatDate(list.created_at)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Users className="w-4 h-4" />
            <span>Shared</span>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <div ref={menuRef} className="absolute top-2 right-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[160px]">
          {isOwner && (
            <>
              <button
                onClick={handleEdit}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleDelete}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </>
          )}
          {!isOwner && onRemoveList && (
            <button
              onClick={handleRemoveList}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Remove list</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ListCard;
